<template>
  <div class="overview">
    <MenuBar />
    <div class="overview-content">
      <div class="header">
        <div class="header-left">
          <h1>Dashboard</h1>
          <p class="welcome-text" v-if="username">👋 Welcome back, <strong>{{ username }}</strong></p>
        </div>
        <button class="logout-btn" @click="logout">🚪 Logout</button>
      </div>

      <div class="section-label">Your Features</div>

      <div class="cards-container">

        <!-- PDF Uploader -->
        <div class="feature-card pdf-card" @click="$router.push('/')">
          <div class="card-icon">📄</div>
          <h2>PDF Uploader</h2>
          <p class="card-description">Upload and store PDF files securely in the cloud. Get shareable links instantly.</p>
          <div class="card-features">
            <div class="feature-item">✓ Drag &amp; Drop Upload</div>
            <div class="feature-item">✓ Cloud Storage</div>
            <div class="feature-item">✓ Public Links</div>
            <div class="feature-item">✓ Instant Preview</div>
          </div>
          <button class="card-btn pdf-btn">Go to PDF Uploader →</button>
        </div>

        <!-- Social Report -->
        <div class="feature-card social-card" @click="$router.push('/smm-report')">
          <div class="card-icon">📊</div>
          <h2>Social Report Uploader</h2>
          <p class="card-description">Analyze and track social media performance with detailed analytics and reports.</p>
          <div class="card-features">
            <div class="feature-item">✓ CSV Analytics</div>
            <div class="feature-item">✓ Date Range Reports</div>
            <div class="feature-item">✓ Post Type Breakdown</div>
            <div class="feature-item">✓ Performance Metrics</div>
          </div>
          <button class="card-btn social-btn">Go to Social Report →</button>
        </div>

        <!-- Campaign Dashboard -->
        <div class="feature-card campaign-card" @click="$router.push('/campaign-dashboard')">
          <div class="card-icon">📈</div>
          <h2>Campaign Dashboard</h2>
          <p class="card-description">Monitor all active and past campaign statuses in one real-time dashboard.</p>
          <div class="card-features">
            <div class="feature-item">✓ Live Status Tracking</div>
            <div class="feature-item">✓ Campaign Timeline</div>
            <div class="feature-item">✓ Multi-Campaign View</div>
            <div class="feature-item">✓ Filter &amp; Search</div>
          </div>
          <button class="card-btn campaign-btn">Go to Campaign Dashboard →</button>
        </div>

        <!-- DCM Management -->
        <div v-if="canAccessDcmManagement" class="feature-card dcm-mgmt-card" @click="$router.push('/dcm-management')">
          <div class="card-icon">🎬</div>
          <h2>DCM Management</h2>
          <p class="card-description">Manage DCM creatives, review uploads, and oversee digital campaign materials.</p>
          <div class="card-features">
            <div class="feature-item">✓ Creative Review</div>
            <div class="feature-item">✓ Approval Workflow</div>
            <div class="feature-item">✓ Status Tracking</div>
            <div class="feature-item">✓ Upload History</div>
          </div>
          <button class="card-btn dcm-mgmt-btn">Go to DCM Management →</button>
        </div>

        <!-- Upload Studio -->
        <div v-if="canAccessDcmDashboard" class="feature-card studio-card" @click="$router.push('/upload-studio')">
          <div class="card-icon">📤</div>
          <h2>Upload Studio</h2>
          <p class="card-description">Upload and submit DCM creatives for review directly from the studio interface.</p>
          <div class="card-features">
            <div class="feature-item">✓ Bulk Upload</div>
            <div class="feature-item">✓ Creative Tagging</div>
            <div class="feature-item">✓ Submission Queue</div>
            <div class="feature-item">✓ File Validation</div>
          </div>
          <button class="card-btn studio-btn">Go to Upload Studio →</button>
        </div>

        <!-- DCM Dashboard -->
        <div v-if="canAccessDcmDashboard" class="feature-card dcm-card" @click="$router.push('/dcm-dashboard')">
          <div class="card-icon">📥</div>
          <h2>DCM Dashboard</h2>
          <p class="card-description">Track all DCM uploads, view processing status, and manage campaign delivery.</p>
          <div class="card-features">
            <div class="feature-item">✓ Upload Tracking</div>
            <div class="feature-item">✓ Delivery Status</div>
            <div class="feature-item">✓ Campaign Mapping</div>
            <div class="feature-item">✓ Error Reports</div>
          </div>
          <button class="card-btn dcm-btn">Go to DCM Dashboard →</button>
        </div>

        <!-- Creative Dashboard -->
        <div v-if="canAccessCreativeDashboard" class="feature-card creative-card" @click="$router.push('/creative-dashboard')">
          <div class="card-icon">🎨</div>
          <h2>Creative Dashboard</h2>
          <p class="card-description">View creative briefs, review assigned tasks, and submit creative assets for campaigns.</p>
          <div class="card-features">
            <div class="feature-item">✓ Brief Management</div>
            <div class="feature-item">✓ Asset Submission</div>
            <div class="feature-item">✓ Feedback Loop</div>
            <div class="feature-item">✓ Revision Tracking</div>
          </div>
          <button class="card-btn creative-btn">Go to Creative Dashboard →</button>
        </div>

        <!-- Sponsor Management -->
        <div v-if="canAccessCampaignSetup" class="feature-card sponsor-card" @click="$router.push('/sponsor-management')">
          <div class="card-icon">🤝</div>
          <h2>Sponsor Management</h2>
          <p class="card-description">Manage sponsors, configure campaign budgets, and track sponsorship deliverables.</p>
          <div class="card-features">
            <div class="feature-item">✓ Sponsor Profiles</div>
            <div class="feature-item">✓ Budget Tracking</div>
            <div class="feature-item">✓ Deliverables Log</div>
            <div class="feature-item">✓ Contract Status</div>
          </div>
          <button class="card-btn sponsor-btn">Go to Sponsor Management →</button>
        </div>

        <!-- Sponsor Reports -->
        <div v-if="canAccessCampaignSetup" class="feature-card sponsor-report-card" @click="$router.push('/sponsor-reports')">
          <div class="card-icon">🔗</div>
          <h2>Sponsor Reports</h2>
          <p class="card-description">Generate and share sponsor performance reports via secure public links.</p>
          <div class="card-features">
            <div class="feature-item">✓ Report Generation</div>
            <div class="feature-item">✓ Public Share Links</div>
            <div class="feature-item">✓ Custom Branding</div>
            <div class="feature-item">✓ Export Options</div>
          </div>
          <button class="card-btn sponsor-report-btn">Go to Sponsor Reports →</button>
        </div>

        <!-- Admin Panel -->
        <div v-if="canManageUsers" class="feature-card admin-card" @click="$router.push('/admin')">
          <div class="card-icon">🔐</div>
          <h2>Admin Panel</h2>
          <p class="card-description">Manage users, assign roles, configure feature access, and reset passwords.</p>
          <div class="card-features">
            <div class="feature-item">✓ User Management</div>
            <div class="feature-item">✓ Role Assignment</div>
            <div class="feature-item">✓ Feature Toggles</div>
            <div class="feature-item">✓ Password Reset</div>
          </div>
          <button class="card-btn admin-btn">Go to Admin Panel →</button>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import api from '../api';
import MenuBar from './MenuBar.vue';

export default {
  name: 'Overview',
  components: {
    MenuBar
  },
  data() {
    return {
      isSuperUser: false,
      userRole: '',
      username: '',
      enabledFeatures: []
    };
  },
  computed: {
    canAccessCampaignSetup() {
      const role = String(this.userRole || '').toLowerCase();
      return this.isSuperUser || ['admin', 'marketing', 'markting'].includes(role);
    },
    canAccessDcmManagement() {
      const role = String(this.userRole || '').toLowerCase();
      return this.isSuperUser || ['admin', 'marketing', 'markting', 'dcm'].includes(role);
    },
    canAccessCreativeDashboard() {
      const role = String(this.userRole || '').toLowerCase();
      return this.isSuperUser || role === 'creative';
    },
    canAccessDcmDashboard() {
      const role = String(this.userRole || '').toLowerCase();
      return this.isSuperUser || role === 'dcm';
    },
    canManageUsers() {
      const role = String(this.userRole || '').toLowerCase();
      return this.isSuperUser || role === 'admin' || this.enabledFeatures.includes('users_management');
    }
  },
  async mounted() {
    try {
      const res = await api.get('/check');
      this.isSuperUser = !!res.data.isSuperUser;
      this.userRole = res.data.role || '';
      this.username = res.data.user || '';
      this.enabledFeatures = Array.isArray(res.data.enabledFeatures) ? res.data.enabledFeatures : [];
    } catch (err) {
      console.error('Overview: failed to fetch user info', err);
    }
  },
  methods: {
    async logout() {
      try {
        await api.post('/logout');
        this.$router.push('/login');
      } catch (err) {
        console.error('Logout error:', err);
        this.$router.push('/login');
      }
    }
  }
};
</script>

<style scoped>
.overview {
  display: flex;
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.overview-content {
  flex: 1;
  margin-left: 250px;
  padding: 40px 20px;
  overflow-y: auto;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 30px;
}

.header h1 {
  margin: 0;
  font-size: 36px;
  color: white;
  font-weight: 700;
  letter-spacing: -0.5px;
}

.logout-btn {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-radius: 8px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.logout-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: white;
  transform: translateY(-2px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
}

.header-left {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.welcome-text {
  margin: 0;
  font-size: 15px;
  color: rgba(255, 255, 255, 0.85);
}

.welcome-text strong {
  color: white;
}

.section-label {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 20px;
}

.cards-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 24px;
}

.feature-card {
  background: white;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.feature-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
}

.feature-card.pdf-card {
  border-top: 4px solid #3498db;
}

.feature-card.pdf-card:hover {
  border-top-color: #2980b9;
  background: linear-gradient(135deg, rgba(52, 152, 219, 0.05) 0%, rgba(52, 152, 219, 0.02) 100%);
}

.feature-card.social-card {
  border-top: 4px solid #e74c3c;
}

.feature-card.social-card:hover {
  border-top-color: #c0392b;
  background: linear-gradient(135deg, rgba(231, 76, 60, 0.05) 0%, rgba(231, 76, 60, 0.02) 100%);
}

.feature-card.campaign-card {
  border-top: 4px solid #27ae60;
}

.feature-card.campaign-card:hover {
  background: linear-gradient(135deg, rgba(39, 174, 96, 0.05) 0%, rgba(39, 174, 96, 0.02) 100%);
}

.feature-card.dcm-mgmt-card {
  border-top: 4px solid #8e44ad;
}

.feature-card.dcm-mgmt-card:hover {
  background: linear-gradient(135deg, rgba(142, 68, 173, 0.05) 0%, rgba(142, 68, 173, 0.02) 100%);
}

.feature-card.studio-card {
  border-top: 4px solid #16a085;
}

.feature-card.studio-card:hover {
  background: linear-gradient(135deg, rgba(22, 160, 133, 0.05) 0%, rgba(22, 160, 133, 0.02) 100%);
}

.feature-card.dcm-card {
  border-top: 4px solid #2980b9;
}

.feature-card.dcm-card:hover {
  background: linear-gradient(135deg, rgba(41, 128, 185, 0.05) 0%, rgba(41, 128, 185, 0.02) 100%);
}

.feature-card.creative-card {
  border-top: 4px solid #f39c12;
}

.feature-card.creative-card:hover {
  background: linear-gradient(135deg, rgba(243, 156, 18, 0.05) 0%, rgba(243, 156, 18, 0.02) 100%);
}

.feature-card.sponsor-card {
  border-top: 4px solid #1abc9c;
}

.feature-card.sponsor-card:hover {
  background: linear-gradient(135deg, rgba(26, 188, 156, 0.05) 0%, rgba(26, 188, 156, 0.02) 100%);
}

.feature-card.sponsor-report-card {
  border-top: 4px solid #2ecc71;
}

.feature-card.sponsor-report-card:hover {
  background: linear-gradient(135deg, rgba(46, 204, 113, 0.05) 0%, rgba(46, 204, 113, 0.02) 100%);
}

.feature-card.admin-card {
  border-top: 4px solid #c0392b;
}

.feature-card.admin-card:hover {
  background: linear-gradient(135deg, rgba(192, 57, 43, 0.05) 0%, rgba(192, 57, 43, 0.02) 100%);
}

.card-icon {
  font-size: 48px;
  text-align: center;
  line-height: 1;
}

.feature-card h2 {
  margin: 0;
  font-size: 24px;
  color: #2c3e50;
  font-weight: 700;
}

.card-description {
  margin: 0;
  font-size: 14px;
  color: #7f8c8d;
  line-height: 1.6;
}

.card-features {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin: 16px 0;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
}

.feature-item {
  font-size: 13px;
  color: #2c3e50;
  font-weight: 500;
  line-height: 1.4;
}

.feature-item::before {
  content: '';
  display: inline;
}

.card-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 16px;
  width: 100%;
}

.feature-card.pdf-card .card-btn,
.pdf-btn {
  background: linear-gradient(135deg, #3498db 0%, #2980b9 100%);
}

.feature-card.social-card .card-btn,
.social-btn {
  background: linear-gradient(135deg, #e74c3c 0%, #c0392b 100%);
}

.campaign-btn { background: linear-gradient(135deg, #27ae60 0%, #229954 100%); }
.dcm-mgmt-btn { background: linear-gradient(135deg, #8e44ad 0%, #7d3c98 100%); }
.studio-btn   { background: linear-gradient(135deg, #16a085 0%, #138d75 100%); }
.dcm-btn      { background: linear-gradient(135deg, #2980b9 0%, #1a6fa0 100%); }
.creative-btn { background: linear-gradient(135deg, #f39c12 0%, #d68910 100%); }
.sponsor-btn  { background: linear-gradient(135deg, #1abc9c 0%, #17a589 100%); }
.sponsor-report-btn { background: linear-gradient(135deg, #2ecc71 0%, #27ae60 100%); }
.admin-btn    { background: linear-gradient(135deg, #c0392b 0%, #a93226 100%); }

.card-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.card-btn:active {
  transform: translateY(0);
}

/* Responsive: Tablet */
@media (max-width: 768px) {
  .overview-content {
    margin-left: 200px;
    padding: 30px 15px;
  }

  .header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    margin-bottom: 30px;
  }

  .header h1 {
    font-size: 28px;
  }

  .cards-container {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .feature-card {
    padding: 24px;
  }

  .card-features {
    grid-template-columns: 1fr;
  }
}

/* Responsive: Mobile */
@media (max-width: 480px) {
  .overview {
    flex-direction: column;
  }

  .overview-content {
    margin-left: 0;
    padding: 20px 12px;
  }

  .header {
    margin-bottom: 24px;
  }

  .header h1 {
    font-size: 24px;
  }

  .logout-btn {
    padding: 8px 16px;
    font-size: 12px;
    width: 100%;
  }

  .welcome-section {
    margin-bottom: 24px;
  }

  .welcome-text {
    font-size: 16px;
  }

  .cards-container {
    gap: 16px;
  }

  .feature-card {
    padding: 20px;
    gap: 12px;
  }

  .card-icon {
    font-size: 40px;
  }

  .feature-card h2 {
    font-size: 20px;
  }

  .card-description {
    font-size: 13px;
  }

  .card-features {
    padding: 12px;
    gap: 8px;
    margin: 12px 0;
  }

  .feature-item {
    font-size: 12px;
  }

  .card-btn {
    padding: 10px 16px;
    font-size: 13px;
  }
}
</style>
